import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { createJournalEntry, updateJournalEntry, getAccounts } from '../services/api';
import { useDialogClickOutside, useModalScrollLock } from '../hooks';
import { Input, CustomSelect, Button } from '@redpanda/ui';
import { FormFieldWithError } from './forms';
import { toast } from 'react-hot-toast';
import { useAccountStore } from '../stores/accountStore';
import { useJournalStoreBase } from '../stores/journalStore';

interface JournalDialogProps {
  isOpen: boolean;
  onClose: () => void;
  journal?: any;
  onJournalCreated?: (savedJournal?: any, isEdit?: boolean) => void;
}

interface JournalDetail {
  accountId: string;
  debitAmount: number | string;
  creditAmount: number | string;
  description: string;
}

interface FormData {
  date: string;
  description: string;
  reference: string;
  details: JournalDetail[];
}

interface Account {
  id: string;
  code: string;
  name: string;
  type: string;
}

const JournalDialog: React.FC<JournalDialogProps> = ({
  isOpen,
  onClose,
  journal,
  onJournalCreated
}) => {
  const { register, handleSubmit, formState: { errors }, reset, control, watch, setValue } = useForm<FormData>({
    defaultValues: {
      date: new Date().toISOString().slice(0, 10),
      description: '',
      reference: '',
      details: [
        { accountId: '', debitAmount: '', creditAmount: '', description: '' },
        { accountId: '', debitAmount: '', creditAmount: '', description: '' }
      ]
    }
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'details'
  });
  
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const { refreshData } = useAccountStore();
  
  const { t } = useTranslation();
  
  // 鎖定背景滾動
  useModalScrollLock(isOpen);

  const handleClose = () => {
    setSubmitError(null);
    setDetailsError(null);
    reset();
    onClose();
  };

  // 點擊外部關閉 dialog
  const handleBackgroundClick = useDialogClickOutside(handleClose);

  // 載入會計科目
  useEffect(() => {
    if (isOpen) {
      const loadAccounts = async () => {
        try {
          setLoadingAccounts(true);
          const response = await getAccounts(1, 1000, true); // 載入所有啟用的科目
          setAccounts(Array.isArray(response.data.data) ? response.data.data : []);
        } catch (error) {
          console.error('Failed to load accounts:', error);
          toast.error(t('journals.toast.loadAccountsFailed'));
        } finally {
          setLoadingAccounts(false);
        }
      };
      loadAccounts();
    }
  }, [isOpen]);

  // 設置表單初始值
  useEffect(() => {
    if (isOpen && journal) {
      setValue('date', journal.date.slice(0, 10));
      setValue('description', journal.description);
      setValue('reference', journal.reference || '');
      setValue('details', journal.details.map((detail: any) => ({
        accountId: detail.accountId,
        debitAmount: detail.debitAmount || 0,
        creditAmount: detail.creditAmount || 0,
        description: detail.description || ''
      })));
    }
  }, [isOpen, journal, setValue]);

  const watchedDetails = watch('details');

  // 計算借貸總額
  const totalDebit = watchedDetails.reduce((sum, detail) => sum + (parseFloat(detail.debitAmount?.toString()) || 0), 0);
  const totalCredit = watchedDetails.reduce((sum, detail) => sum + (parseFloat(detail.creditAmount?.toString()) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const handleAddDetail = () => {
    append({ accountId: '', debitAmount: '', creditAmount: '', description: '' });
  };

  const handleRemoveDetail = (index: number) => {
    if (fields.length > 2) {
      remove(index);
    }
  };

  const onSubmit = async (data: FormData) => {
    setSubmitError(null);
    setDetailsError(null);

    // 過濾掉空的明細
    const validDetails = data.details.filter(detail => {
      const debitAmount = parseFloat(detail.debitAmount?.toString()) || 0;
      const creditAmount = parseFloat(detail.creditAmount?.toString()) || 0;
      return detail.accountId && (debitAmount > 0 || creditAmount > 0);
    });

    if (validDetails.length < 2) {
      setDetailsError(t('journals.dialog.minDetails'));
      return;
    }

    const journalData = {
      date: new Date(data.date).toISOString(),
      description: data.description.trim(),
      reference: data.reference?.trim() || undefined,
      details: validDetails.map(detail => ({
        accountId: detail.accountId,
        debitAmount: parseFloat(detail.debitAmount?.toString()) || 0,
        creditAmount: parseFloat(detail.creditAmount?.toString()) || 0,
        description: detail.description,
      })),
    };

    const isEdit = !!journal;

    if (isEdit) {
      // UPDATE 樂觀更新
      const originalJournal = { ...journal };
      const optimisticJournal = {
        ...journal,
        ...journalData,
        details: journalData.details.map((d, idx) => ({
          ...d,
          id: journal.details?.[idx]?.id || `temp-detail-${idx}`,
          account: journal.details?.[idx]?.account || { id: d.accountId, code: '', name: '', type: '' },
        })),
        updatedAt: new Date().toISOString(),
      };

      useJournalStoreBase.getState().updateItem(journal.id, optimisticJournal);
      handleClose();
      if (onJournalCreated) onJournalCreated(optimisticJournal, true);

      try {
        const response = await updateJournalEntry(journal.id, journalData);
        const realData = response.data || optimisticJournal;
        useJournalStoreBase.getState().updateItem(journal.id, realData);
        toast.success(t('common.messages.updateSuccess'));
        refreshData();
      } catch (error: any) {
        useJournalStoreBase.getState().updateItem(journal.id, originalJournal);
        const msg = error.response?.data?.error || error.response?.data?.errors?.map((e: any) => e.msg).join(', ') || t('journals.toast.updateFailed');
        toast.error(msg);
      }
    } else {
      // CREATE 樂觀更新
      const tempId = `temp-${Date.now()}`;
      const tempJournal = {
        id: tempId,
        date: journalData.date,
        description: journalData.description,
        reference: journalData.reference,
        totalAmount: totalDebit,
        isPosted: false,
        details: journalData.details.map((d, idx) => ({
          id: `temp-detail-${idx}`,
          accountId: d.accountId,
          debitAmount: d.debitAmount,
          creditAmount: d.creditAmount,
          description: d.description,
          account: {
            id: d.accountId,
            code: '',
            name: accounts.find(a => a.id === d.accountId)?.name || '',
            type: '',
          },
        })),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      useJournalStoreBase.getState().addItem(tempJournal as any);
      handleClose();
      if (onJournalCreated) onJournalCreated(tempJournal as any, false);

      try {
        const response = await createJournalEntry(journalData);
        const realData = response.data || tempJournal;
        useJournalStoreBase.getState().updateItem(tempId, realData);
        toast.success(t('common.messages.createSuccess'));
        refreshData();
      } catch (error: any) {
        useJournalStoreBase.setState((state: any) => ({
          items: state.items.filter((i: any) => i.id !== tempId),
          pagination: state.pagination ? {
            ...state.pagination,
            totalItems: Math.max(0, state.pagination.totalItems - 1),
          } : null,
        }));
        const msg = error.response?.data?.error || error.response?.data?.errors?.map((e: any) => e.msg).join(', ') || t('journals.toast.createFailed');
        toast.error(msg);
      }
    }
  };

  const accountOptions = (accounts || []).map(account => ({
    value: account.id,
    label: `${account.code} - ${account.name}`
  }));

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onMouseDown={handleBackgroundClick}
    >
      <div 
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-visible"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 對話框標題 */}
        <div className="flex justify-between items-center px-6 py-3 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {journal ? t('journals.dialog.title.edit') : t('journals.dialog.title.add')}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* 對話框內容 */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4 overflow-visible">

          {/* 基本資訊 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormFieldWithError
              label={t('journals.dialog.transactionDate')}
              required={true}
              error={errors.date?.message}
              htmlFor="date"
            >
              <div 
                className="relative cursor-pointer"
                onClick={() => {
                  const dateInput = document.getElementById('date') as HTMLInputElement;
                  if (dateInput) {
                    dateInput.focus();
                    dateInput.showPicker?.();
                  }
                }}
              >
                <Input
                  type="date"
                  id="date"
                  {...register('date', { required: t('common.forms.validation.required') })}
                  variant={errors.date ? "error" : "default"}
                  className="cursor-pointer"
                />
              </div>
            </FormFieldWithError>

            <div className="md:col-span-2">
              <FormFieldWithError
                label={t('journals.dialog.descriptionLabel')}
                required={true}
                error={errors.description?.message}
                htmlFor="description"
              >
                <Input
                  type="text"
                  id="description"
                  {...register('description', { required: t('common.forms.validation.required') })}
                  variant={errors.description ? "error" : "default"}
                />
              </FormFieldWithError>
            </div>
          </div>

          <FormFieldWithError
            label={t('journals.dialog.reference')}
            required={false}
            helpText={journal ? undefined : t('journals.dialog.referenceHint')}
            error={errors.reference?.message}
            htmlFor="reference"
          >
            <Input
              type="text"
              id="reference"
              {...register('reference')}
              variant={errors.reference ? "error" : "default"}
            />
          </FormFieldWithError>

          {/* 分錄明細 */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('journals.dialog.details')} <span className="ml-1">*</span>
                </label>
                {detailsError && (
                  <span className="text-sm text-red-600 dark:text-red-400 font-medium">
                    {detailsError}
                  </span>
                )}
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleAddDetail}
                className="inline-flex items-center"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                {t('journals.dialog.addDetail')}
              </Button>
            </div>

            {/* 標題行 */}
            <div className="grid grid-cols-12 gap-4 items-center mb-4">
              <div className="col-span-4">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('journals.dialog.account')}
                </span>
              </div>
              <div className="col-span-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('journals.dialog.debitAmount')}
                </span>
              </div>
              <div className="col-span-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('journals.dialog.creditAmount')}
                </span>
              </div>
              <div className="col-span-3">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('journals.dialog.description')}
                </span>
              </div>
              <div className="col-span-1">
                {/* 操作列 */}
              </div>
            </div>

            <div className="space-y-4">
              {fields.map((field, index) => (
                <div key={field.id} className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-4">
                    <div className="space-y-1">
                      <CustomSelect
                        {...register(`details.${index}.accountId` as const)}
                        value={watch(`details.${index}.accountId`)}
                        onChange={(value) => setValue(`details.${index}.accountId`, value)}
                        placeholder={t('journals.dialog.selectAccount')}
                        options={accountOptions}
                        disabled={loadingAccounts}
                      />
                    </div>
                  </div>
                  
                  <div className="col-span-2">
                    <div className="space-y-1">
                      <Input
                        type="text"
                        placeholder="0"
                        {...register(`details.${index}.debitAmount` as const)}
                      />
                    </div>
                  </div>
                  
                  <div className="col-span-2">
                    <div className="space-y-1">
                      <Input
                        type="text"
                        placeholder="0"
                        {...register(`details.${index}.creditAmount` as const)}
                      />
                    </div>
                  </div>
                  
                  <div className="col-span-3">
                    <div className="space-y-1">
                      <Input
                        type="text"
                        {...register(`details.${index}.description` as const)}
                      />
                    </div>
                  </div>
                  
                  <div className="col-span-1 flex items-center">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => handleRemoveDetail(index)}
                      disabled={fields.length <= 2}
                      className="w-full"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* {t('journals.dialog.balanced')}檢查 */}
            <div className="mt-4 p-4 bg-gray-50 dark:bg-slate-700 rounded-lg">
              <div className="flex justify-between items-center">
                <div className="text-sm">
                  <span className="text-gray-700 dark:text-gray-300">{t('journals.dialog.debitTotal')}：</span>
                  <span className="font-mono font-medium ml-2">${Math.round(totalDebit)}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-700 dark:text-gray-300">{t('journals.dialog.creditTotal')}：</span>
                  <span className="font-mono font-medium ml-2">${Math.round(totalCredit)}</span>
                </div>
                <div className={`text-sm font-medium ${
                  isBalanced 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-red-600 dark:text-red-400'
                }`}>
                  {isBalanced ? `✓ ${t('journals.dialog.balanced')}` : `✗ ${t('journals.dialog.unbalanced')}`}
                </div>
              </div>
            </div>
          </div>

          {/* 錯誤訊息 */}
          {submitError && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{submitError}</p>
            </div>
          )}

          {/* 按鈕 */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-slate-700">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-md"
              onClick={handleClose}
            >
              {t('journals.dialog.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={!isBalanced}
              className="inline-flex justify-center rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
            >
              {journal ? t('journals.dialog.actions.update') : t('journals.dialog.actions.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JournalDialog;